import { Button } from '@/components/ui/shadcn/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/shadcn/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/shadcn/popover';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { Check, Target } from 'lucide-react';
import { useState } from 'react';

export interface LeadOption {
    id: string;
    lead_number?: string | null;
    name?: string | null;
    /** A lead with no customer cannot carry a calculation — those entries are offered but not selectable. */
    company_id?: string | null;
    company_name?: string | null;
}

interface LeadSelectProps {
    leads: LeadOption[];
    value: string | null;
    onValueChange: (value: string | null) => void;
    disabled?: boolean;
    className?: string;
    /** Grey out leads with no customer, with `unusableNote` as the reason. */
    requireCompany?: boolean;
    labels?: {
        placeholder?: string;
        search?: string;
        empty?: string;
        heading?: string;
        unusableNote?: string;
    };
}

export function leadLabel(lead: LeadOption | null | undefined): string {
    if (!lead) {
        return '';
    }

    return lead.lead_number?.trim() || lead.name?.trim() || lead.id;
}

function leadSubtitle(lead: LeadOption): string {
    // lead_number already leads the row, so the name only repeats itself when it is
    // the fallback label.
    const parts = lead.lead_number ? [lead.name, lead.company_name] : [lead.company_name];

    return parts.filter(Boolean).join(' · ');
}

/**
 * Lead picker, built to the same contract as PersonSelect and
 * CalculationRequestSelect: a searchable popover over a caller-supplied list.
 *
 * Used on the calculation-request create form, where a lead is mandatory.
 */
export function LeadSelect({ leads, value, onValueChange, disabled = false, className, requireCompany = false, labels }: LeadSelectProps) {
    const { t } = useTranslations();
    const [open, setOpen] = useState(false);

    const placeholder = labels?.placeholder ?? t('common.lead_select.placeholder', 'Select a lead');
    const searchLabel = labels?.search ?? t('common.lead_select.search', 'Search leads…');
    const emptyLabel = labels?.empty ?? t('common.lead_select.empty', 'No leads found.');
    const heading = labels?.heading ?? t('common.lead_select.heading', 'Leads');
    const unusableNote = labels?.unusableNote ?? t('common.lead_select.no_company', 'No customer set on this lead');

    const selected = value ? (leads.find((lead) => lead.id === value) ?? null) : null;

    const select = (id: string) => {
        onValueChange(id === value ? null : id);
        setOpen(false);
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    disabled={disabled}
                    className={cn('h-auto w-full justify-start gap-2 py-2 font-normal', !selected && 'text-muted-foreground', className)}
                >
                    <Target className="text-muted-foreground h-4 w-4 shrink-0" />
                    <span className="truncate text-sm">
                        {selected ? leadLabel(selected) : placeholder}
                        {selected && leadSubtitle(selected) ? <span className="text-muted-foreground"> — {leadSubtitle(selected)}</span> : null}
                    </span>
                </Button>
            </PopoverTrigger>

            <PopoverContent className="w-[--radix-popover-trigger-width] min-w-80 p-0" align="start">
                <Command>
                    <CommandInput placeholder={searchLabel} />
                    <CommandList>
                        <CommandEmpty>{emptyLabel}</CommandEmpty>
                        <CommandGroup heading={heading}>
                            {leads.map((lead) => {
                                const unusable = requireCompany && !lead.company_id;
                                const subtitle = leadSubtitle(lead);

                                return (
                                    <CommandItem
                                        key={lead.id}
                                        value={`${leadLabel(lead)} ${lead.name ?? ''} ${lead.company_name ?? ''} ${lead.id}`}
                                        disabled={unusable}
                                        onSelect={() => {
                                            if (!unusable) {
                                                select(lead.id);
                                            }
                                        }}
                                        className={cn('items-start gap-2', unusable && 'opacity-50')}
                                    >
                                        <Target className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
                                        <span className="flex min-w-0 flex-1 flex-col">
                                            <span className="truncate text-sm font-medium">{leadLabel(lead)}</span>
                                            {subtitle ? <span className="text-muted-foreground truncate text-xs">{subtitle}</span> : null}
                                            {unusable ? <span className="text-muted-foreground truncate text-xs italic">{unusableNote}</span> : null}
                                        </span>
                                        {lead.id === value ? <Check className="text-primary mt-0.5 h-4 w-4 shrink-0" /> : null}
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
